import React, { useState, useEffect } from 'react';
import { 
  Truck, 
  Users, 
  UserCheck, 
  Phone, 
  Clock,
  AlertTriangle,
  Activity
} from 'lucide-react';
import { motion } from 'framer-motion';

const Dashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [emergencyAlerts] = useState([
    {
      id: 1,
      type: 'Critical',
      location: 'Downtown Medical Center',
      ambulance: 'AMB-001',
      time: '2 min ago',
      status: 'En Route'
    },
    {
      id: 2,
      type: 'Urgent',
      location: 'City General Hospital',
      ambulance: 'AMB-003',
      time: '5 min ago',
      status: 'On Scene'
    }
  ]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const stats = [
    {
      title: 'Active Ambulances',
      value: '12',
      change: '+2',
      icon: Truck,
      color: 'bg-primary-500',
      trend: 'up'
    },
    {
      title: 'Total Patients',
      value: '156',
      change: '+23',
      icon: Users,
      color: 'bg-accent-500',
      trend: 'up'
    },
    {
      title: 'Medical Staff',
      value: '45',
      change: '+5',
      icon: UserCheck,
      color: 'bg-emerald-400',
      trend: 'up'
    },
    {
      title: 'Emergency Calls',
      value: '8',
      change: '-2',
      icon: Phone,
      color: 'bg-neutral-400',
      trend: 'down'
    }
  ];

  const recentActivities = [
    {
      id: 1,
      type: 'Patient Pickup',
      description: 'Patient John Doe picked up from 123 Main St',
      time: '10:30 AM',
      ambulance: 'AMB-002'
    },
    {
      id: 2,
      type: 'Hospital Arrival',
      description: 'AMB-001 arrived at City General Hospital',
      time: '10:25 AM',
      ambulance: 'AMB-001'
    },
    {
      id: 3,
      type: 'Emergency Call',
      description: 'New emergency call received from Downtown',
      time: '10:20 AM',
      ambulance: 'AMB-003'
    },
    {
      id: 4,
      type: 'Staff Assignment',
      description: 'Dr. Smith assigned to AMB-004',
      time: '10:15 AM',
      ambulance: 'AMB-004'
    }
  ];

  return (
    <div className="p-8 bg-neutral-50 min-h-screen">
      <div className="mb-10">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2 tracking-tight">Dashboard</h1>
        <p className="text-neutral-500">
          Real-time overview of emergency ambulance operations
        </p>
        <div className="flex items-center space-x-2 mt-2 text-sm text-neutral-400">
          <Clock className="w-4 h-4" />
          <span>{currentTime.toLocaleTimeString()}</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card flex flex-col gap-2 items-start justify-between min-h-[140px]"
          >
            <div className="flex items-center justify-between w-full">
              <div>
                <p className="text-sm font-medium text-neutral-500">{stat.title}</p>
                <p className="text-2xl font-bold text-neutral-900 tracking-tight">{stat.value}</p>
                <div className="flex items-center mt-2">
                  <span className={`text-sm font-semibold ${
                    stat.trend === 'up' ? 'text-primary-600' : 'text-red-500'
                  }`}>
                    {stat.change}
                  </span>
                  <span className="text-sm text-neutral-400 ml-1">from last hour</span>
                </div>
              </div>
              <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center shadow-soft`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Emergency Alerts */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="card"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-neutral-900">Emergency Alerts</h2>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-red-600 font-medium">Live</span>
            </div>
          </div>
          
          <div className="space-y-4">
            {emergencyAlerts.map((alert) => (
              <div key={alert.id} className="border-l-4 border-primary-500 pl-4 py-3 bg-primary-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-4 h-4 text-primary-500" />
                      <span className="font-semibold text-primary-800">{alert.type}</span>
                    </div>
                    <p className="text-sm text-neutral-700 mt-1">{alert.location}</p>
                    <p className="text-xs text-neutral-400 mt-1">Ambulance: {alert.ambulance}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-neutral-400">{alert.time}</span>
                    <div className="mt-1">
                      <span className="status-busy">{alert.status}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activities */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="card"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-neutral-900">Recent Activities</h2>
            <Activity className="w-5 h-5 text-neutral-300" />
          </div>
          
          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3 p-3 bg-neutral-50 rounded-xl">
                <div className="w-2 h-2 bg-primary-400 rounded-full mt-2"></div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-neutral-900">{activity.type}</span>
                    <span className="text-xs text-neutral-400">{activity.time}</span>
                  </div>
                  <p className="text-sm text-neutral-600 mt-1">{activity.description}</p>
                  <p className="text-xs text-neutral-400 mt-1">Ambulance: {activity.ambulance}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="mt-10 card"
      >
        <h2 className="text-xl font-semibold text-neutral-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="flex flex-col items-center p-4 bg-primary-50 rounded-xl hover:bg-primary-100 transition-all duration-200">
            <Truck className="w-6 h-6 text-primary-500 mb-2" />
            <span className="text-sm font-semibold text-primary-900">Add Ambulance</span>
          </button>
          <button className="flex flex-col items-center p-4 bg-accent-50 rounded-xl hover:bg-accent-100 transition-all duration-200">
            <Users className="w-6 h-6 text-accent-500 mb-2" />
            <span className="text-sm font-semibold text-accent-900">New Patient</span>
          </button>
          <button className="flex flex-col items-center p-4 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-all duration-200">
            <UserCheck className="w-6 h-6 text-emerald-500 mb-2" />
            <span className="text-sm font-semibold text-emerald-900">Assign Staff</span>
          </button>
          <button className="flex flex-col items-center p-4 bg-neutral-100 rounded-xl hover:bg-neutral-200 transition-all duration-200">
            <Phone className="w-6 h-6 text-neutral-500 mb-2" />
            <span className="text-sm font-semibold text-neutral-900">Emergency Call</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard; 